import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { adminCheckAuth, adminLogout } from '../../services/adminApi';
import {
  HiOutlineHome,
  HiOutlineShoppingBag,
  HiOutlineCollection,
  HiOutlineMenu,
  HiOutlineCog,
  HiOutlineCurrencyDollar,
  HiOutlineLogout,
  HiOutlineX,
} from 'react-icons/hi';
import './admin.css';

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  useEffect(() => {
    adminCheckAuth()
      .then(() => setChecking(false))
      .catch(() => navigate('/admin/login', { replace: true }));
  }, [navigate]);

  // Close sidebar on route change for mobile
  useEffect(() => {
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  }, [location]);

  // Lock body scroll on mobile when sidebar is open
  useEffect(() => {
    if (window.innerWidth <= 768 && sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  const handleLogout = async () => {
    try {
      await adminLogout();
    } catch {
      // ignore
    }
    navigate('/admin/login', { replace: true });
  };

  if (checking) {
    return (
      <div className="admin-loading-screen">
        <div className="admin-spinner" />
      </div>
    );
  }

  const navItems = [
    { to: '/admin', icon: <HiOutlineHome size={20} />, label: 'Dashboard', end: true },
    { to: '/admin/products', icon: <HiOutlineShoppingBag size={20} />, label: 'Products' },
    { to: '/admin/categories', icon: <HiOutlineCollection size={20} />, label: 'Categories' },
    { to: '/admin/navigation', icon: <HiOutlineMenu size={20} />, label: 'Navigation' },
    { to: '/admin/settings', icon: <HiOutlineCog size={20} />, label: 'Site Settings' },
    { to: '/admin/currency', icon: <HiOutlineCurrencyDollar size={20} />, label: 'Currency' },
  ];

  return (
    <div className="admin-layout">
      {sidebarOpen && (
        <div className="admin-sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'collapsed'}`}>
        <div className="admin-sidebar-header">
          <h2>YIBE</h2>
          <span className="admin-sidebar-badge">Admin</span>
          <button className="admin-sidebar-close" onClick={() => setSidebarOpen(false)}>
            <HiOutlineX size={20} />
          </button>
        </div>
        <nav className="admin-sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `admin-nav-link ${isActive ? 'active' : ''}`
              }
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="admin-sidebar-footer">
          <button className="admin-nav-link admin-logout-btn" onClick={handleLogout}>
            <HiOutlineLogout size={20} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
      <main className={`admin-main ${sidebarOpen ? '' : 'admin-main-full'}`}>
        <header className="admin-topbar">
          <button
            className="admin-hamburger"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <HiOutlineMenu size={24} />
          </button>
        </header>
        <div className="admin-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
